import { bookSlot } from '@/labels/serviceRequest';
import {
  computeDates,
  fetchTimeSlotsByDate,
  formatTo12Hrs,
  returnDates,
  transformAvailableSlots,
} from '@/utils/serviceRequest';
import { useEffect, useState, useContext, useCallback, useMemo, useRef } from 'react';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { ProductContext, useProductContext } from '@/context/productContext';
import WebEngageUtils from '@/utils/webengageUtils';
import { ProfileContext } from '@/context/profileContext';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import AppBar from '@/shared/AppBar';
import FixedWrapper from '@/shared/FixedWrapper';
import Map from '../Map';
import classNames from 'classnames';
import DatePickerComp from '../SelectDate/Datepicker';
import Modal from '@/globalComponents/modal';

import BackgroundBlue from '../Icons/BackgroundBlue';
import SelectedDeviceCard from '../AMC/SelectedDeviceCard';
import { useRouter } from 'next/router';
import { useAppConfigContext } from '@/context/AppContext';
import { getSelectedDevice, isApplicableForAMCUpSell } from '@/utils/amcPlans';
import dateUtil from '@/utils/date';
import useScreen from '@/hooks/useScreen';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import { DEFAULT_DEVICE_FAMILY_NAME } from '@/constants/amc';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { IR_WORK_ORDER_QUERY } from '@/constants/app';
import SessionStorage from '@/utils/sessionStorage';
import { rescheduleInstallationRequest } from '@/actions/rescheduleServiceRequest';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { getServiceCost } from '@/actions/fetchServiceCost';
import { STATUS_TRACKING_URL } from '@/configs/constants';
import getSingleString from '@/utils/string';

interface ChooseSlotProps {
  days?: number;
}

export type ServiceSlot = {
  slot: { startTime: Date; endTime: Date };
  workerCodes: string[];
  available: boolean;
  slotDuration: number;
};

const background = 'linear-gradient(180deg, #434343 0%, #CFCFCF 100%)';

const ChooseSlot = (props: ChooseSlotProps) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const { days = 7 } = props;
  const { currentDate, endDate } = returnDates();
  const { dispatch: serviceAction, state: serviceState } = useContext(ServiceRequestContext);

  const { state: productState, dispatch: productDispatch } = useContext(ProductContext);

  const {
    serviceType,
    serviceDate,
    serviceSlot,
    webengageData,
    isRescheduleFlow,
    defectType,
    incidentId,
    workOrderId,
    rescheduleReasonCode,
    rescheduleDescription,
  } = serviceState;

  const { state: profileState } = useContext(ProfileContext);
  const [calnDate, setCalnDate] = useState(currentDate);
  const { state: product, dispatch } = useProductContext();

  const [suggestedSlots, setSuggestedSlots] = useState<ServiceSlot[]>([]);
  const [open, setOpen] = useState(false);
  const landedEventFired = useRef(false);
  const [dateSlots, setDateSlots] = useState<Date[]>([]);
  const [isError, setIsError] = useState<boolean>();
  const { selectedProduct } = productState;
  const { hubCode } = selectedProduct;
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { fireGTMEvent } = useGTM();
  const { isDesktop } = useScreen();
  const {
    state: { config },
  } = useAppConfigContext();

  const { webServiceGeoUpdate } = useFeatureFlags();
  const IRWorkOrderFlow = useMemo(() => {
    return router?.query?.[IR_WORK_ORDER_QUERY];
  }, [router]);

  const fetch = (...args: any) => rescheduleInstallationRequest.apply(null, args);
  const IRReschedule = useTriggerRequest({ fetch });

  useEffect(() => {
    serviceAction.updateIncidentId(incidentId);
  }, [incidentId]);

  const commonGTMEventProperties = {
    screen_name: SCREEN_NAMES.BOOK_A_SLOT,
    reschedule: Boolean(isRescheduleFlow),
    service_type: serviceType,
    time_slot_selected: serviceState.serviceSlot?.slot?.startTime
      ? formatTo12Hrs(new Date(serviceState.serviceSlot?.slot?.startTime))
      : '',
    defect_selected: defectType,
    current_timestamp: webengageData?.current_timestamp,
    selected_date: serviceState.serviceDate?.toDateString(),
    available_slots: suggestedSlots
      .reduce((acc: string[], item) => {
        if (item.available) {
          let slotString: string =
            item.slot.startTime.getHours() + '-' + item.slot.endTime.getHours();
          acc.push(slotString);
        }
        return acc;
      }, [])
      .join(','),
  };

  useEffect(() => {
    let tempDate: Date;
    if (Object.prototype.toString.call(serviceDate) === '[object Date]') {
      if (isNaN(serviceDate.getTime())) {
        tempDate = new Date();
      } else {
        tempDate = serviceDate;
      }
    } else {
      tempDate = new Date();
    }
    serviceAction.updateServiceDate(tempDate);
    setDateSlots(computeDates(tempDate, days));
    serviceAction.setIsRouting(false);
  }, []);

  useEffect(() => {
    if (router.query.screen === 'issue_screen' && router.query.incidentType) {
      serviceAction.updateSRContext({ isFMS: true, incidentType: router.query.incidentType });
    }
  }, [router.query.screen, router.query.incidentType]);

  useEffect(() => {
    setIsLoading(true);
    serviceAction.updateServiceSlot(undefined);
    if (serviceDate)
      fetchTimeSlotsByDate(
        IRWorkOrderFlow && !serviceType ? 'repair' : serviceType,
        serviceDate,
        hubCode,
        selectedProduct,
        profileState,
        id,
        workOrderId,
      )
        .then((slots) => {
          const _slots = transformAvailableSlots(slots);
          setSuggestedSlots(_slots);
          setIsError(false);
          setIsLoading(false);
        })
        .catch(() => {
          setIsError(true);
          setIsLoading(false);
        });
  }, [serviceDate]);

  useEffect(() => {
    if (!landedEventFired.current && suggestedSlots.length) {
      fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_LANDED, {
        ...commonGTMEventProperties,
      });
      landedEventFired.current = true;
    }

    const _slotDiff = WebEngageUtils.slotDiff(suggestedSlots);
    const _slotStr = WebEngageUtils.createSlotStr(suggestedSlots);
    serviceAction.initWebengageContext({
      date_selection_method: 'Calendar',
      current_timestamp: Date.now(),
      available_slots: _slotStr,
      current_time_vs_earliest_available_slot: _slotDiff,
    });
  }, [suggestedSlots]);

  const onClose = () => {
    setCalnDate(serviceDate);
    setOpen(false);
  };
  const onDateChange = (date: Date) => {
    setCalnDate(date);
  };
  const selectDate = () => {
    setDateSlots(computeDates(calnDate, days));
    serviceAction.updateServiceDate(calnDate);
    onClose();
    fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_DATE_SELECTED, {
      ...commonGTMEventProperties,
      date_selection_method: 'Calendar',
    });
    serviceAction.initWebengageContext({
      date_selection_method: 'Calendar',
    });
  };

  const { serviceRequestID } = serviceState;
  const { id, deviceId, deviceFamilyName, address } = selectedProduct;
  const { collectLatLong = false } = address || {};

  const handleContinueClick = async () => {
    if (IRWorkOrderFlow && isRescheduleFlow) {
      const reqBody = {
        workOrderId,
        incidentId,
        preferredSlot: serviceSlot?.slot?.startTime,
        description: '',
        reason_code: rescheduleReasonCode,
        reason_comments: rescheduleDescription,
      };
      const IRrescheduleStatus = await IRReschedule.promisifyTrigger(reqBody);
      if (IRrescheduleStatus === 200) {
        serviceAction.changeComponent('bookServiceSuccess');
      }
    } else if (collectLatLong && webServiceGeoUpdate) {
      serviceAction.changeComponent('mapLocator');
    } else if (isApplicableForAMCUpSell(config, serviceType, selectedProduct, isRescheduleFlow)) {
      router.push({
        pathname: `/amc-service-plans/${id}/${deviceId}`,
        query: {
          assets: true,
          deviceFamilyName: deviceFamilyName ?? DEFAULT_DEVICE_FAMILY_NAME,
        },
      });
    } else if (
      (status === 'IW' ||
        status === 'IC' ||
        (['reinstall', 'uninstall', 'dismantling'].includes(serviceType.toLowerCase()) &&
          (status === 'OW' || status === 'OC'))) &&
      serviceState.isPrepaidAllowed
    ) {
      router.push({
        pathname: `/service/${id}/prepaid`,
        query: { schedulePrepaid: true },
      });
    } else serviceAction.changeComponent('serviceReqSummary');

    fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_CONTINUE_CLICKED, {
      ...commonGTMEventProperties,
    });
  };

  const { expiryStatus } = product?.selectedProduct ?? {};

  const { status } = expiryStatus || {};

  const handleCardClick = () => {
    setOpen(true);
    setCalnDate(serviceDate);
  };

  const onDeviceChange = () => {
    fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_DEVICE_PICKER_SELECTED, {
      ...commonGTMEventProperties,
    });
    serviceAction.changeComponent('bookAService');
  };

  const backButtonHAndler = useCallback(() => {
    const { reschedule, reqID } = router?.query || {};
    if (reschedule && IRWorkOrderFlow) {
      router.push({
        pathname: STATUS_TRACKING_URL,
        query: {
          reqId: reqID,
          [IR_WORK_ORDER_QUERY]: true,
        },
      });
    } else if (reschedule) {
      router.push({
        pathname: '/statusTracking/',
        query: {
          reqId: serviceRequestID,
          assetid: id,
        },
      });
    } else if (serviceState.serviceType === 'reinstall') {
      serviceAction.changeComponent('reInstallForm');
    } else serviceAction.changeComponent('bookAService');
    fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_BACK_CLICKED, {
      ...commonGTMEventProperties,
    });
  }, [serviceAction, serviceState.serviceType, router, serviceRequestID, id, fireGTMEvent]);

  useEffect(() => {
    // TODO : update the response functions once the api starts functioning
    async function fetchAndSetServiceCost() {
      if (selectedProduct?.id) {
        try {
          const incidentTypeId = getSingleString(incidentId);
          const response = await getServiceCost(selectedProduct.id, incidentTypeId);
          const { productId, materialId, amount, productName, isPrepaidAllowed } = response;

          const cost = amount;
          serviceAction.updateSRContext({ servicePrice: amount });
          serviceAction.updateSRContext({
            isPrepaidAllowed,
            materialId,
            productId,
            productName,
          });
        } catch (e) {
          console.error(e);
        }
      }
    }
    if (!isRescheduleFlow) fetchAndSetServiceCost();
  }, [selectedProduct?.id, isRescheduleFlow, incidentId]);

  return (
    <>
      <BackgroundBlue className="fixed max-h-screen left-0 top-0 overflow-hidden hidden lg:block" />
      <BackgroundBlue className="fixed max-h-screen right-0 top-0 overflow-hidden hidden lg:block rotate-180" />
      <Div className="-mt-8 relative">
        <AppBar
          fixed
          transparent
          onClick={backButtonHAndler}
          onScrollLabel="Preferred date & time "
          preventScrollBackground={isDesktop}
        />
        <Div className="w-full max-w-screen-sm mx-auto">
          <Div className="flex flex-col mb-10 pt-16 w-80">
            <Paragraph className="text-h-1 md:text-hd-1 mb-3 text-black/70">
              Preferred date & time
            </Paragraph>
            <Paragraph className="text-body-lg text-black/50">
              This is not a confirmed slot. A technician will be assigned close to your preferred
              slot.
            </Paragraph>
          </Div>
          <Div className="flex justify-between md:justify-start items-center">
            <Paragraph className="text-h-3 text-black/70">{bookSlot.selectDateLabel}</Paragraph>

            {/* <Button
              theme="link"
              className="md:hidden"
              data-testid="mobile-change-date"
              onClick={handleCardClick}
            >
              More Dates
            </Button> */}
          </Div>
          <Div className="flex flex-col gap-10  md:mx-auto">
            <Modal
              id="selectDate"
              title="Select Date"
              onClose={onClose}
              open={open}
              classNames={
                'min-h-[auto] md:!min-h-[250px] max-w-sm m-[auto] max-[320px]:w-[19rem] max-[320px]:mx-[0.5rem]'
              }
            >
              <DatePickerComp
                maxDate={endDate}
                selectedDate={calnDate}
                currDate={currentDate}
                modalClose={onClose}
                setStartDate={onDateChange}
              />
              <div className="flex flex-col w-full">
                <Button
                  theme="primary"
                  className="box-border m-[1rem] mt-[0rem]"
                  onClick={selectDate}
                  disabled={false}
                >
                  Confirm
                </Button>
              </div>
            </Modal>
            <Div className="mt-4 md:mt-6">
              <Div className=" flex overflow-scroll hide-scrollbar md:flex-wrap items-start gap-s5 -mx-6 px-6 md:px-0 md:mx-0">
                <Map
                  list={dateSlots}
                  render={(date: Date, index) => {
                    const isSelected =
                      date.setHours(0, 0, 0, 0) - serviceDate?.setHours(0, 0, 0, 0) === 0;
                    if (date < endDate) {
                      return (
                        <Div
                          key={date.toString()}
                          className="p-px rounded-cm bg-neutral-50 cursor-pointer"
                          style={
                            isSelected
                              ? {
                                  background,
                                }
                              : {}
                          }
                        >
                          <Div className="bg-white rounded-cm">
                            <Button
                              theme="none"
                              className={classNames(
                                'flex  justify-center bg-white p-4  rounded-cm',
                                {
                                  'border-gray-400 shadow-medium': isSelected,
                                  ' hover:border-black hover:border-1': !isSelected,
                                },
                              )}
                              onClick={() => {
                                setCalnDate(date);
                                fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_DATE_SELECTED, {
                                  date_selection_method: 'Quick View',
                                  ...commonGTMEventProperties,
                                });
                                serviceAction.updateServiceDate(date);
                                serviceAction.initWebengageContext({
                                  date_selection_method: 'Quick View',
                                });
                              }}
                              style={
                                isSelected
                                  ? {
                                      background:
                                        'linear-gradient(127.01deg, #E8F2FF 10.96%, rgba(232, 242, 255, 0)',
                                    }
                                  : {}
                              }
                              key={date.toDateString()}
                              data-testid={`day-${index}`}
                            >
                              <Div className="flex flex-col justify-center gap-s3 items-center w-20">
                                <Paragraph
                                  className={classNames('text-body-xs font-normal', {
                                    '!font-bold': isSelected,
                                  })}
                                >
                                  {dateUtil
                                    .format(date, {
                                      month: 'short',
                                      year: undefined,
                                      day: undefined,
                                    })
                                    .toUpperCase()}
                                </Paragraph>
                                <Paragraph
                                  className={classNames('text-h-2 font-normal ', {
                                    '!font-bold': isSelected,
                                  })}
                                >
                                  {date.getDate()}
                                </Paragraph>
                                <Paragraph
                                  className={classNames('font-normal ', {
                                    '!font-bold': isSelected,
                                  })}
                                >
                                  {dateUtil.format(date, {
                                    month: undefined,
                                    year: undefined,
                                    day: undefined,
                                    weekday: 'long',
                                  })}
                                </Paragraph>
                              </Div>
                            </Button>
                          </Div>
                        </Div>
                      );
                    }
                  }}
                />
              </Div>
              {/* <Button
                theme="link"
                onClick={handleCardClick}
                className="w-fit hidden md:block "
                data-testid="desktop-change-date"
              >
                More Dates
              </Button> */}
            </Div>

            <Div>
              <Paragraph className="text-h-3 mb-4 text-black/70" data-testid="time-label">
                {bookSlot.selectTimeLabel}
              </Paragraph>
              <div className={`${isError ? 'block' : 'grid'} grid-cols-2 md:grid-cols-3 gap-s5`}>
                {isError && !isLoading ? (
                  <p data-testid="time-slot-error">{bookSlot.timeSlotError}</p>
                ) : (
                  !!suggestedSlots.length && (
                    <Map
                      list={suggestedSlots}
                      render={(suggestedSlot: ServiceSlot, index) => {
                        const isSelected =
                          suggestedSlot.slot.startTime.getHours() ===
                            serviceSlot?.slot.startTime.getHours() &&
                          suggestedSlot.slot.startTime.getMinutes() ===
                            serviceSlot?.slot.startTime.getMinutes();

                        const formatedStartTime = formatTo12Hrs(suggestedSlot.slot.startTime);
                        const formattedEndTime = formatTo12Hrs(suggestedSlot.slot.endTime);
                        return (
                          <Div
                            key={`${formatedStartTime}-${formattedEndTime}`}
                            className="p-px rounded-cm bg-neutral-50 cursor-pointer"
                            style={
                              isSelected
                                ? {
                                    background,
                                  }
                                : {}
                            }
                          >
                            <Div className="bg-white rounded-cm h-full">
                              <Button
                                className={classNames(
                                  'p-s4 md:p-s5 text-button-normal flex justify-center w-full bg-white items-center rounded-cm',
                                  {
                                    'line-through text-neutral-50':
                                      !suggestedSlot.available || isLoading,
                                    ' border-gray-400 shadow-medium': isSelected,
                                    'hover:bg-light-blue-100 hover:border-black hover:border-1':
                                      !isSelected,
                                  },
                                )}
                                onClick={() => {
                                  fireGTMEvent(GTMEventNames.BOOK_A_SLOT_SCREEN_TIME_SELECTED, {
                                    ...commonGTMEventProperties,
                                    time_slot_selected: formatedStartTime,
                                  });
                                  serviceAction.updateServiceSlot(suggestedSlot);
                                }}
                                style={
                                  isSelected
                                    ? {
                                        background:
                                          'linear-gradient(127.01deg, #E8F2FF 10.96%, rgba(232, 242, 255, 0)',
                                      }
                                    : {}
                                }
                                theme="none"
                                key={suggestedSlot.slot.startTime.getTime()}
                                dataTestId={`time-slot-${index}`}
                                disabled={!suggestedSlot.available}
                              >
                                <Paragraph
                                  dataTestId={`time-${index}`}
                                  className={classNames({
                                    'font-extrabold': isSelected,
                                  })}
                                >
                                  {formatedStartTime} - {formattedEndTime}
                                </Paragraph>
                              </Button>
                            </Div>
                          </Div>
                        );
                      }}
                    />
                  )
                )}
              </div>
            </Div>
          </Div>
          <FixedWrapper className="p-6 pt-4 md:pb-4  bg-white shadow-subtle">
            <Div className="flex gap-s5 flex-col max-w-screen-sm mx-auto">
              {IRWorkOrderFlow && isRescheduleFlow ? null : (
                <SelectedDeviceCard
                  className="md:border md:p-4 md:pb-1 md:rounded-cm"
                  onDeviceChange={onDeviceChange}
                  isRescheduleJourney={Boolean(router.query.reschedule)}
                />
              )}
              <Button
                theme="primary"
                className="w-full max-w-screen-sm mx-auto"
                onClick={handleContinueClick}
                disabled={!serviceDate || !serviceSlot}
                isLoading={isLoading || IRReschedule?.isLoading}
                dataTestId="choose-slot-next-button"
              >
                Continue
              </Button>
            </Div>
          </FixedWrapper>
        </Div>
      </Div>
    </>
  );
};

export default ChooseSlot;
